import type { ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import type { ChipProps } from '@mui/material';
import type { ActionItemStatus, NewsletterStatus } from '@/types';

export function LoadingState({ label = 'Loading…' }: { label?: string }) {
  return (
    <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ py: 8 }}>
      <CircularProgress />
      <Typography color="text.secondary">{label}</Typography>
    </Stack>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <Alert
      severity="error"
      action={
        onRetry ? (
          <Button color="inherit" size="small" onClick={onRetry}>
            Retry
          </Button>
        ) : undefined
      }
      sx={{ my: 2 }}
    >
      {message}
    </Alert>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderStyle: 'dashed' }}>
      <Typography variant="h3" gutterBottom>
        {title}
      </Typography>
      {description && (
        <Typography color="text.secondary" sx={{ mb: action ? 3 : 0 }}>
          {description}
        </Typography>
      )}
      {action}
    </Paper>
  );
}

export function SectionCard({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h3">{title}</Typography>
        {action}
      </Stack>
      <Box>{children}</Box>
    </Paper>
  );
}

const NEWSLETTER_STATUS_COLOR: Record<NewsletterStatus, ChipProps['color']> = {
  draft: 'default',
  published: 'success',
};

const ACTION_ITEM_STATUS_COLOR: Record<ActionItemStatus, ChipProps['color']> = {
  open: 'default',
  in_progress: 'info',
  blocked: 'error',
  completed: 'success',
};

export function NewsletterStatusChip({ status }: { status: NewsletterStatus }) {
  return (
    <Chip
      size="small"
      label={status === 'published' ? 'Published' : 'Draft'}
      color={NEWSLETTER_STATUS_COLOR[status]}
      variant={status === 'published' ? 'filled' : 'outlined'}
    />
  );
}

export function ActionItemStatusChip({ status, label }: { status: ActionItemStatus; label: string }) {
  return (
    <Chip
      size="small"
      label={label}
      color={ACTION_ITEM_STATUS_COLOR[status]}
      variant={status === 'open' ? 'outlined' : 'filled'}
    />
  );
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  destructive = false,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Dialog open={open} onClose={onCancel}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{message}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button onClick={onConfirm} color={destructive ? 'error' : 'primary'} variant="contained">
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
